// TODO: Implement an actual poge for support
import {
  IdentificationIcon,
  DownloadIcon,
  KeyIcon,
  DocumentSearchIcon,
  PencilAltIcon,
  ChevronDoubleDownIcon,
  ChevronDownIcon,
  ChatAlt2Icon,
  SupportIcon,
  DocumentTextIcon,
  NewspaperIcon,
  MenuAlt2Icon,
  ClipboardIcon,
} from "@heroicons/react/outline";
import Link from "next/link";
import Image from "next/image";
import PageDefault from "../components/PageDefault";
import ContentPlacer from "../components/ContentPlacer";
import { Disclosure } from "@headlessui/react";
import DottedBg from "../components/pageUI/DottedBg";
import SupportVideo from "../components/pageUI/SupportVideo";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const stepsList = [
  {
    id: 1,
    name: "Create account",
    description:
      "Register with our service by creating an account. Please use an active email adress so we can verify & contact you if required.",
    icon: IdentificationIcon,
  },
  {
    id: 2,
    name: "Download extension",
    description:
      "Install the SummerEyes extension from the Chrome Web Store. You will be prompted to approve a set of permissions to enable the extension - these are necessary to provide a great user experience.",
    icon: DownloadIcon,
  },
  {
    id: 3,
    name: "Sign in to extension",
    description:
      "Open the extension by clicking the icon in the top-right of the Chrome browser. Sign in using the credentials registered in step  through the official website. You'll have to sign in to the extension even if you're signed in to our website.",
    icon: KeyIcon,
  },
  {
    id: 4,
    name: "Summarize",
    description:
      'SummerEyes will popup on any page we think we can assist. Hover over the icon and select "Summarize this for me" for instant summaries, right in the webpage.',
    icon: DocumentSearchIcon,
  },
];

const summaryAvenues = [
  {
    id: 1,
    name: "Summarize an entire webpage",
    description: `The AI companion will popup on any page we think we can assist. Hover over the SummerEyes icon and select "Summarize this for me" for instant summaries, right in the webpage.`,
    icon: DocumentTextIcon,
  },
  {
    id: 2,
    name: "Summarize specific highlighted text on a webpage",
    description: `Any text you can select with your cursor may also be summarized. Select text as if you were about to copy it, right-click, and select "Summarize this for me" from the menu. We'll then generate a summary just as we would for a full webpage.`,
    icon: MenuAlt2Icon,
  },
  {
    id: 3,
    name: "Summarize text through copy-paste",
    description: `Want to summarize some text that's not in your web browser? No worries. Open up our extension by clicking the icon to the right of the URL bar & navigate to the "Summarize" tab. You can paste any text in the box and we'll create a summary of it.`,
    icon: ClipboardIcon,
  },
];

const commonIssues = [
  {
    question: "Any time I try to summarize something it tells me to sign in?",
    answer:
      "We require you to log in to the Chrome Extension as well as the website. Even though you may be signed in to this site, you will still need to enter your details into the extension itself to access the functionality. You can do this by opening the extension (Click the puzzle icon on the top-right of your browser) & entering in your registered details.  ",
  },
  {
    question: "Some features or menu items don't show up?",
    answer:
      "Unfortunately some IT policies prohibit the installation & execution of certain Chrome Extensions. To resolve this issue, please reach out to your IT department to whitelist our application.",
  },
  {
    question: "It won't let me install the extension through the link?",
    answer:
      "At present we can only be installed on desktop versions of Google Chrome or Brave browsers. Google prevents the installation of any extension on a mobile device.",
  },
  {
    question: "My summary has weird punctuation or repeated phrases?",
    answer:
      "We try experiment with a variety of algorithms & parameters to find the best way to summarize text for you. Unfortunately, this means that sometimes we get it wrong. If your summary is unsatisfactory, please make sure to provide feedback so that we can note performance of the model used & improve your user experience.",
  },
];

const supportLinks = [
  {
    name: "FAQs",
    href: "/faqs",
    description:
      "Looking for answers? We've got you covered. Have a read through our frequently asked questions for further info on key topics.",
    linkText: "Take me there",
    icon: ChatAlt2Icon,
  },
  {
    name: "Technical Support",
    href: "mailto:support@summereyes.ai?subject=Hi there! I need some technical support.",
    description:
      "Having issues? Finding bugs? Drop us an email and we can work together to sort it out. We're here to help.",
    linkText: "Contact us",
    icon: SupportIcon,
  },
  {
    name: "Feedback",
    href: "mailto:feedback@summereyes.ai?subject=Hi there! Here's some feedback from a user.",
    description:
      "Want to share your thoughts? We love receiving feedback from our users. Send us an email with any praise, criticism, suggestions, and ideas.",
    linkText: "Contact us",
    icon: PencilAltIcon,
  },
];

export default function Support({ session }) {
  return (
    <>
      <PageDefault pageTitle="Support | SummerEyes" setBackground="bg-gray-50">
        <DottedBg />
        <div className="overflow-hidden bg-gray-50 pt-16 lg:pt-12">
          <section className="sm:pb-20">
            <ContentPlacer>
              <div className="relative mx-auto lg:max-w-7xl ">
                <svg
                  className="absolute left-full hidden -translate-x-1/2 -translate-y-1/4 transform lg:block"
                  width={404}
                  height={784}
                  fill="none"
                  viewBox="0 0 404 784"
                  aria-hidden="true"
                >
                  <defs>
                    <pattern
                      id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7"
                      x={0}
                      y={0}
                      width={20}
                      height={20}
                      patternUnits="userSpaceOnUse"
                    >
                      <rect
                        x={0}
                        y={0}
                        width={4}
                        height={4}
                        className="text-gray-200"
                        fill="currentColor"
                      />
                    </pattern>
                  </defs>
                  <rect
                    width={404}
                    height={784}
                    fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)"
                  />
                </svg>

                <div className="justify-left flex">
                  <div className="justify-left relative w-full md:w-3/4">
                    <h2 className="text-left text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-5xl">
                      You're 2 minutes away from{" "}
                      <span className="bg-gradient-to-tr from-summer-light-highlight to-summer-bright bg-clip-text text-transparent">
                        next-level productivity
                      </span>{" "}
                    </h2>
                    <p className="mt-4 max-w-3xl text-left text-xl text-gray-500">
                      Follow the steps below to get started. Not working out?
                      We're here to help. <br />
                      <Link href="/faqs">
                        <a className="text-summer-blue-400 hover:text-summer-blue-600">
                          Read our FAQs
                        </a>
                      </Link>
                      , or get in contact with us.
                    </p>
                  </div>
                </div>

                <div className="relative z-20 mt-8">
                  <div className="z-30 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="col-span-full pt-6 ">
                      <div className="flow-root rounded-lg border border-gray-200 bg-white px-6 pb-8 shadow-xl">
                        <div className="-mt-6">
                          <div>
                            <span className="inline-flex items-center justify-center rounded-md bg-summer-bright p-3 shadow-lg">
                              <ChevronDoubleDownIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                              />
                            </span>
                          </div>

                          <p className="mt-5 text-base text-gray-700">
                            SummerEyes is an AI productivity tool that can be
                            downloaded as a Chrome Extension. We require you to
                            register an account{" "}
                            <Link href="/register">
                              <a className="text-summer-light">here</a>
                            </Link>
                            , and then use the same account details to sign in
                            to the extension, which can be downloaded{" "}
                            <Link href="https://chrome.google.com/webstore/detail/summereyes-ai-productivit/hipjhgcpienlgjnecbhdaipgghicigei">
                              <a
                                className="text-summer-light"
                                target={"_blank"}
                              >
                                here
                              </a>
                            </Link>
                            .
                            <br />
                            Once set up, you can use your companion to summarize
                            any text in the web browser.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="justify-left flex">
                  <div className="justify-left relative w-full pt-5 md:w-3/4">
                    <p className="mt-4 max-w-3xl text-left text-xl text-summer-bright">
                      Setup
                    </p>
                    <h2 className="text-left text-3xl font-medium leading-8 tracking-tight text-gray-900 sm:text-4xl">
                      Installing & accessing SummerEyes
                    </h2>
                  </div>
                </div>

                <div className="relative mt-10 lg:mt-10 lg:grid lg:grid-cols-2 lg:items-center lg:gap-8">
                  <div
                    className="mx-auto my-10 h-full w-full rounded-xl border border-gray-200 bg-white p-5 shadow-xl md:order-last lg:mt-0"
                    aria-hidden="true"
                  >
                    <SupportVideo />
                  </div>
                  <div className="relative">
                    <dl className="space-y-10">
                      {stepsList.map((item) => (
                        <div key={item.id} className="relative">
                          <dt>
                            <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-summer-bright text-white">
                              <item.icon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </div>
                            <p className="ml-16 text-lg font-medium leading-6 text-gray-900">
                              {item.name}
                            </p>
                          </dt>
                          <dd className="mt-2 ml-16 text-base text-gray-500">
                            {item.description}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </div>

              <div className="justify-left flex">
                <div className="justify-left relative w-full pt-5 md:w-3/4">
                  <p className="mt-4 max-w-3xl text-left text-xl text-summer-bright">
                    Features
                  </p>
                  <h2 className="text-left text-3xl font-medium leading-8 tracking-tight text-gray-900 sm:text-4xl">
                    Accessing summaries in your browser
                  </h2>
                </div>
              </div>

              <div className="my-6">
                <p className="text-gray-700">
                  We offer three avenues to summarize text online. Let's walk
                  through them together.
                </p>
              </div>

              <div className="relative mt-10 lg:mt-10 lg:items-center lg:gap-8">
                <div className="relative">
                  <dl className="space-y-10">
                    {summaryAvenues.map((item) => (
                      <div key={item.id} className="relative">
                        <dt>
                          <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-summer-bright text-white">
                            <item.icon className="h-6 w-6" aria-hidden="true" />
                          </div>
                          <p className="ml-16 text-lg font-medium leading-6 text-gray-900">
                            {item.name}
                          </p>
                        </dt>
                        <dd className="mt-2 ml-16 text-base text-gray-500">
                          {item.description}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="justify-left flex">
                <div className="justify-left relative w-full pt-10 md:w-3/4">
                  <p className="mt-4 max-w-3xl text-left text-xl text-summer-bright">
                    Troubleshooting
                  </p>
                  <h2 className="text-left text-3xl font-medium leading-8 tracking-tight text-gray-900 sm:text-4xl">
                    Common issues{" "}
                  </h2>
                </div>
              </div>

              <div className="relative mt-4 lg:mt-4 lg:items-center lg:gap-8">
                <div className="relative">
                  <dl className="mt-6 space-y-6 divide-y divide-gray-200">
                    {commonIssues.map((commonIssue) => (
                      <Disclosure
                        as="div"
                        key={commonIssue.question}
                        className="pt-6"
                      >
                        {({ open }) => (
                          <>
                            <dt className="text-lg">
                              <Disclosure.Button className="flex w-full items-start text-left text-gray-400">
                                <span className="mr-6 flex h-7 items-center">
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? "-rotate-180" : "rotate-0",
                                      "h-6 w-6 transform"
                                    )}
                                    aria-hidden="true"
                                  />
                                </span>
                                <span className="font-medium text-gray-900">
                                  {commonIssue.question}
                                </span>
                              </Disclosure.Button>
                            </dt>
                            <Disclosure.Panel as="dd" className="mt-2 pr-12">
                              <p className="text-base text-gray-500">
                                {commonIssue.answer}
                              </p>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    ))}
                  </dl>
                </div>
              </div>
            </ContentPlacer>
          </section>

          {/* Section 2: Links to contact & other key items */}
          <div className="mt-4 h-auto bg-summer-base pt-4 pb-16">
            <ContentPlacer>
              <div className="relative mx-auto mt-16 max-w-md px-4 pb-32 sm:max-w-3xl sm:px-6 md:mt-16 lg:max-w-7xl lg:px-8">
                <h1 className="text-5xl font-medium tracking-tight text-white">
                  Still struggling?
                </h1>
                <p className="mt-6 max-w-3xl text-2xl text-gray-200">
                  We're here to help.
                </p>
              </div>

              <div className="bg-blue-gray-50 mt-20">
                {/* Cards */}
                <section
                  className="relative z-10 mx-auto -mt-32 max-w-md px-4 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8"
                  aria-labelledby="contact-heading"
                >
                  <h2 className="sr-only" id="contact-heading">
                    Contact us
                  </h2>
                  <div className="grid grid-cols-1 gap-y-20 lg:grid-cols-3 lg:gap-y-0 lg:gap-x-8">
                    {supportLinks.map((link) => (
                      <div
                        key={link.name}
                        className="flex flex-col rounded-2xl bg-white shadow-xl"
                      >
                        <div className="relative flex-1 px-6 pt-16 pb-8 md:px-8">
                          <div className="absolute top-0 inline-block -translate-y-1/2 transform rounded-xl bg-summer-bright p-5 shadow-lg">
                            <link.icon
                              className="h-6 w-6 text-white"
                              aria-hidden="true"
                            />
                          </div>
                          <h3 className="text-blue-gray-900 text-2xl font-bold">
                            {link.name}
                          </h3>
                          <p className="text-blue-gray-500 mt-4 text-base">
                            {link.description}
                          </p>
                        </div>
                        <div className="bg-summer-blue-50 rounded-bl-2xl rounded-br-2xl p-6 md:px-8">
                          <Link href={link.href}>
                            <a className="text-base font-medium text-summer-bright hover:text-summer-light">
                              {link.linkText}
                            </a>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </ContentPlacer>
          </div>
        </div>
      </PageDefault>
    </>
  );
}
